import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSpaceSkill, useCreateSpaceSkill, useUpdateSpaceSkill } from '../../hooks/useSkills';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Button, Card, CardHeader, Input, Select, Textarea } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import type { SkillStatus, CreateSkillInput, UpdateSkillInput } from '../../types';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'archived', label: 'Archived' },
];

export default function SkillForm() {
  const { skillId } = useParams<{ skillId: string }>();
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();

  const isEditing = !!skillId;

  const { data: existingSkill, isLoading: skillLoading } = useSpaceSkill(skillId);
  const createSkill = useCreateSpaceSkill();
  const updateSkill = useUpdateSpaceSkill();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instructions: '',
    status: 'active' as SkillStatus,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (existingSkill && isEditing) {
      setFormData({
        name: existingSkill.name,
        description: existingSkill.description,
        instructions: existingSkill.instructions,
        status: existingSkill.status,
      });
    }
  }, [existingSkill, isEditing]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.instructions.trim()) {
      newErrors.instructions = 'Instructions are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      if (isEditing && skillId) {
        const input: UpdateSkillInput = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          instructions: formData.instructions.trim(),
          status: formData.status,
        };
        await updateSkill.mutateAsync({ skillId, input });
        success('Skill updated successfully');
        navigate(routes.console.spaceSkill(spaceId, skillId));
      } else {
        const input: CreateSkillInput = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          instructions: formData.instructions.trim(),
        };
        const newSkill = await createSkill.mutateAsync(input);
        success('Skill created successfully');
        navigate(routes.console.spaceSkill(spaceId, newSkill.skillId));
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save skill');
    }
  };

  if (isEditing && skillLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const isSubmitting = createSkill.isPending || updateSkill.isPending;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {isEditing ? 'Edit Skill' : 'Create Skill'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isEditing
            ? 'Update your skill configuration'
            : 'Create a new reusable skill'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <Card>
          <CardHeader title="Basic Information" />
          <div className="space-y-4">
            <Input
              label="Name"
              required
              value={formData.name}
              onChange={(e: { target: { value: string } }) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              error={errors.name}
              placeholder="My Skill"
            />
            <Textarea
              label="Description"
              required
              value={formData.description}
              onChange={(e: { target: { value: string } }) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              error={errors.description}
              placeholder="A brief description that will be shown in the system prompt"
              hint="This description is injected into the agent's system prompt"
              rows={2}
            />
            {isEditing && (
              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={formData.status}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: e.target.value as SkillStatus,
                  }))
                }
              />
            )}
          </div>
        </Card>

        {/* Instructions */}
        <Card>
          <CardHeader
            title="Instructions"
            description="Markdown content that will be loaded when this skill is activated"
          />
          <Textarea
            required
            value={formData.instructions}
            onChange={(e: { target: { value: string } }) =>
              setFormData((prev) => ({ ...prev, instructions: e.target.value }))
            }
            error={errors.instructions}
            placeholder="# Skill Instructions

Write detailed instructions here. This content will be loaded into the agent's context when the skill is activated.

## Guidelines
- Use markdown formatting
- Be specific about expected behavior
- Include examples if helpful"
            rows={16}
            className="font-mono text-sm"
          />
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Link to={isEditing ? routes.console.spaceSkill(spaceId, skillId!) : routes.console.spaceSkills(spaceId)}>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
          <Button type="submit" isLoading={isSubmitting}>
            {isEditing ? 'Save Changes' : 'Create Skill'}
          </Button>
        </div>
      </form>
    </div>
  );
}
